import { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { useEnterpriseEmulation } from '@/hooks/useEnterpriseEmulation';
import {
  clearEnterpriseEmulationStash,
  readEnterpriseEmulationStash,
} from '@/lib/enterprise-emulation';

const POLL_MS = 30_000;
const ADMIN_REMINDER_MS = 60 * 60 * 1000; // hourly "still emulating" toast

interface ServerContext {
  audit_id: string;
  admin_user_id: string;
  partner_id: string;
  partner_name: string;
  started_at: string;
  read_only: boolean;
  reason: string;
  mode: 'enterprise_admin';
}

/**
 * Persistent AMBER banner for any session involved in an enterprise-admin
 * emulation. Distinct from the super-admin RED ImpersonationBanner — color
 * AND a Building2 icon disambiguate "enterprise admin" from "BuildIt staff"
 * for the member (spec §6.4, accessibility).
 *
 * Detection drives off the server context RPC
 * (current_enterprise_emulation_context), keyed to target_user_id =
 * auth.uid(). It renders whenever that RPC returns a row REGARDLESS of stash
 * presence (spec §6.7), because the MEMBER-concurrent-login case has NO stash
 * — that is the member's only in-product signal that an admin is in their
 * account. Do NOT add a "skip the RPC when stash is absent" optimization.
 *
 * Self-clearing guard (spec §6.4.1): if a stash is present but serverCtx is
 * null or its audit_id mismatches, clear OUR OWN stash and do not render —
 * so a stale/foreign stash can't make the wrong banner stick. Each loop
 * touches only __buildit_enterprise_emulation_v1.
 *
 * The Stop control + hourly "still emulating" reminder are ADMIN-side only
 * (they require the stash to restore the admin session); the member-concurrent
 * banner is informational.
 */
export function EnterpriseEmulationBanner(): JSX.Element | null {
  const { end, isEnding } = useEnterpriseEmulation();
  const { toast } = useToast();
  const [serverCtx, setServerCtx] = useState<ServerContext | null>(null);
  // Whether the current session is the ADMIN-driven (target JWT) session,
  // i.e. a matching stash is present. Drives the Stop control + reminder.
  const [adminDriven, setAdminDriven] = useState(false);
  const [targetEmail, setTargetEmail] = useState<string | null>(null);
  const lastReminderRef = useRef<number>(Date.now());

  const refresh = useCallback(async () => {
    const { data, error } = await supabase.rpc('current_enterprise_emulation_context');
    if (error) {
      // Auth-transient gateway errors during the session swap — treat as no
      // context (mirror useEnterpriseEmulation.callServerContext). Don't page.
      const isAuthTransient =
        error.code?.startsWith('PGRST3') || /jwt/i.test(error.message ?? '');
      if (!isAuthTransient) {
        captureError(
          new Error(
            `current_enterprise_emulation_context failed: ${error.message} (${error.code})`,
          ),
          { feature: 'enterprise-emulation-context', code: error.code },
        );
      }
      setServerCtx(null);
      setAdminDriven(false);
      return;
    }

    const ctx = (data as ServerContext | null) ?? null;
    const stash = readEnterpriseEmulationStash();

    if (!ctx) {
      // No open emulation for this session. Self-clearing guard: if a stale
      // stash lingers, clear it. Render nothing.
      if (stash) clearEnterpriseEmulationStash();
      setServerCtx(null);
      setAdminDriven(false);
      setTargetEmail(null);
      return;
    }

    // A row exists (member-concurrent OR admin-driven). Render the banner.
    setServerCtx(ctx);

    if (stash) {
      if (stash.audit_id === ctx.audit_id) {
        // Admin-driven session: stash matches the open row.
        setAdminDriven(true);
        setTargetEmail(stash.target_email);
      } else {
        // Foreign/stale stash — clear it, but still render the (member) banner.
        clearEnterpriseEmulationStash();
        setAdminDriven(false);
        setTargetEmail(null);
      }
    } else {
      // Member-concurrent-login: no stash, server context only.
      setAdminDriven(false);
      setTargetEmail(null);
    }
  }, []);

  const handleStop = useCallback(async () => {
    try {
      await end();
      toast({ title: 'Emulation ended', description: 'Restored your admin session.' });
    } catch (err) {
      captureError(err as Error, { feature: 'enterprise-emulation-end' });
      toast({
        title: 'Could not restore admin session',
        description: 'Please sign in again with your admin account.',
        variant: 'destructive',
      });
      // end() clears the stash before it throws; force a clean exit to /login
      // rather than leaving the user stuck behind a banner whose Stop button
      // is now a no-op. Short delay so the toast is readable first.
      setTimeout(() => window.location.assign('/login'), 1500);
    }
  }, [end, toast]);

  // Poll the server context. Runs regardless of stash presence (spec §6.7).
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) void refresh();
    };
    run();
    const id = setInterval(run, POLL_MS);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        setTimeout(run, 0);
      }
    });
    return () => {
      cancelled = true;
      clearInterval(id);
      subscription.unsubscribe();
    };
  }, [refresh]);

  // Keyboard exit (admin-driven only).
  useEffect(() => {
    if (!adminDriven) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        void handleStop();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [adminDriven, handleStop]);

  // Admin-side "wrong account" reminder — the no-cap analog of the super-admin
  // 55-min warning. Hourly "Still emulating {email}" toast (spec §6.4).
  useEffect(() => {
    if (!adminDriven) return;
    lastReminderRef.current = Date.now();
    const id = setInterval(() => {
      if (Date.now() - lastReminderRef.current >= ADMIN_REMINDER_MS) {
        lastReminderRef.current = Date.now();
        toast({
          title: 'Still emulating',
          description: targetEmail
            ? `You are still acting as ${targetEmail}.`
            : 'You are still acting as a member account.',
        });
      }
    }, 60_000);
    return () => clearInterval(id);
  }, [adminDriven, targetEmail, toast]);

  if (!serverCtx) return null;

  return (
    <div
      role="alert"
      className="sticky top-0 z-[100] w-full bg-amber-600 text-white shadow-md"
    >
      <div className="mx-auto max-w-7xl px-4 py-2 flex items-center gap-3">
        <Building2 className="h-4 w-4 shrink-0" aria-hidden />
        <div className="flex-1 min-w-0 text-sm">
          {adminDriven ? (
            <>
              <span className="font-medium">Emulating </span>
              <span className="font-mono truncate">{targetEmail}</span>
              <span className="opacity-90 hidden sm:inline">
                {' · '}
                {serverCtx.partner_name}
              </span>
            </>
          ) : (
            <span>
              An administrator from{' '}
              <span className="font-medium">{serverCtx.partner_name}</span> is viewing
              your account.
            </span>
          )}
        </div>
        {adminDriven && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleStop}
            disabled={isEnding}
            className="text-white hover:bg-white/20 h-7 px-2"
          >
            <X className="h-3.5 w-3.5 mr-1" />
            {isEnding ? 'Stopping…' : 'Stop emulating'}
          </Button>
        )}
      </div>
    </div>
  );
}
